import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Button, { ButtonProps } from '../../../button';
import { CgArrowsExchangeAlt } from '@react-icons/all-files/cg/CgArrowsExchangeAlt';
import { DeleteButton } from '../../../button/delete-button';
import { FaRegTrashAlt } from '@react-icons/all-files/fa/FaRegTrashAlt';
import { useTableContext } from '../with-provider';
import { SearchField } from '../../../input';

export interface TableAction {
  label: string;
  onClick: (selectedIds: any[]) => void;
  icon?: React.ReactNode;
  color?: ButtonProps['color'];
  variant?: ButtonProps['variant'];
  buttonProps?: Omit<ButtonProps, 'onClick' | 'color' | 'variant'>;
}

export const withExternalActions =
  (Component: React.FC<any>) =>
  ({
    selectPlaceholder = 'Comparar',
    selectButtonProps,
    onDeleteSelected,
    onSelectItems,
    actions,
    tableActions,
    handleSearch,
    ...props
  }: IExternalActionsProps) => {
    if (
      !onSelectItems &&
      !onDeleteSelected &&
      !actions &&
      !tableActions?.length &&
      !props.enableSearch
    )
      return <Component {...props} />;

    const { selectedRows, onSearch, table } = useTableContext();
    const disabledButton = !selectedRows?.length;

    const handleExternalFunction = () => {
      const data = selectedRows.map((row) => row?.id || row?.identifier);

      onSelectItems!(data);
      table.resetRowSelection();
    };

    const getColor = (color = 'primary') => {
      return disabledButton ? 'inherit' : (color as any);
    };

    const handleDeleteSelected = async () => {
      const data = selectedRows.map((row) => row?.id || row?.identifier);

      if (onDeleteSelected) {
        await onDeleteSelected(data);
      }

      table.resetRowSelection();
    };

    const handleActionClick = (action: TableAction) => {
      const data = selectedRows.map((row) => row?.id || row?.identifier);
      action.onClick(data);
      table.resetRowSelection();
    };

    return (
      <Box>
        <Divider sx={{ my: 1.5 }} />
        <Stack
          direction={{
            xs: 'column-reverse',
            md: 'row',
          }}
          spacing={1}
        >
          <Stack
            direction="row"
            justifyContent={{
              md: 'space-between',
            }}
            flex={1}
            spacing={1}
          >
            <Stack spacing={1} direction="row" flexWrap="wrap">
              {onSelectItems && (
                <Button
                  variant="contained"
                  color={getColor(selectButtonProps?.color)}
                  startIcon={<CgArrowsExchangeAlt />}
                  {...selectButtonProps}
                  disabled={disabledButton}
                  onClick={handleExternalFunction}
                >
                  {selectPlaceholder}
                </Button>
              )}

              {tableActions?.map((action, index) => (
                <Button
                  key={`table-action-${index}`}
                  variant={action.variant || 'contained'}
                  color={getColor(action.color)}
                  startIcon={action.icon}
                  {...action.buttonProps}
                  disabled={disabledButton}
                  onClick={() => handleActionClick(action)}
                >
                  {action.label}
                </Button>
              ))}

              {onDeleteSelected && (
                <Box>
                  <DeleteButton
                    title={`Excluir ${selectedRows.length} itens selecionados`}
                    subTitle="Tem certeza que deseja excluir os itens selecionados?"
                    onDelete={handleDeleteSelected}
                    disabled={disabledButton}
                  >
                    <Button
                      variant="contained"
                      color="error"
                      startIcon={<FaRegTrashAlt />}
                      disabled={disabledButton}
                      fullWidthMobile
                    >
                      Excluir selecionadas
                    </Button>
                  </DeleteButton>
                </Box>
              )}
              {actions}
            </Stack>

            <Box flexGrow={1}>{props.tableFilters}</Box>
          </Stack>
          {props.enableSearch && (
            <SearchField
              placeholder={props.searchPlaceHolder}
              handleSearch={handleSearch || onSearch}
              defaultValue={props.searchValue}
              autoFocus={false}
            />
          )}
        </Stack>

        <Component {...props} />
      </Box>
    );
  };

export interface IExternalActionsProps {
  selectPlaceholder?: string;
  selectButtonProps?: ButtonProps;
  actions?: React.ReactNode;
  tableActions?: TableAction[];
  enableSearch?: boolean;
  filterButtonText?: string;
  tableFilters?: React.ReactNode;
  onSelectItems?: (data: any[]) => void;
  onDeleteSelected?: (data: any[]) => Promise<void>;
  handleSearch?: (value: string | undefined) => void;
  searchPlaceHolder?: string;
  searchValue?: string;
  variant?: 'list' | 'table';
  onChangeSelectedRows?: (rows: any[]) => void;
}
